import { computed, unref } from 'vue';
import { CODES, useLiveQueryV2 } from '@libs/use';
import { InstrumentSectorResult } from '@libs/api-reference';
import { MaybeRef } from '@vueuse/core';

import {
  SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX,
  SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX,
  SYMBOL_QUERY_KEYS,
  SYMBOL_TRADE_CHANNEL_PRE_OPENING_PREFIX,
  SYMBOL_TRADE_CHANNEL_PREFIX,
} from '../constants';
import {
  transformClosingPriceChange,
  transformPreOpeningTradePriceChange,
  transformSymbolTradingStateChange,
  transformTradePriceChange,
} from '../utils/transforms';
import * as API from '../requests';

export type Classification = {
  sectorCodes?: string | Array<string | number>;
  name?: string;
  types?: string | Array<string | number>;
  subSectorCodes?: string | Array<string | number> | null;
  instrumentGroupIds?: string | Array<string | number>;
};

export default function useLiveClassificationSymbolsItemsQuery(
  options: Classification,
  { immediate }: { immediate: MaybeRef<boolean> } = { immediate: false }
) {
  const {
    result,
    execute: getClassificationSymbolsItemList,
    isPending: isLoadingClassificationSymbolsItems,
    isSuccess: isSuccessLoadingClassificationSymbolsItems,
    isError: isErrorLoadingClassificationSymbolsItems,
    status: classificationSymbolsItemsStatus,
  } = useLiveQueryV2<InstrumentSectorResult>({
    fetcher: () => API.getClassificationSymbolsItems(unref(options)),
    key: SYMBOL_QUERY_KEYS.symbolClassificationItems(options),
    immediate,
    getChannels: (result) => {
      if (result?.data.result) {
        return result.data.result.reduce(
          (symbolsChannels: string[], symbol) => {
            symbolsChannels.push(
              `${SYMBOL_TRADE_CHANNEL_PREFIX}${symbol.isin}`,
              `${SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX}${symbol.isin}`,
              `${SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX}${symbol.isin}`,
              `${SYMBOL_TRADE_CHANNEL_PRE_OPENING_PREFIX}${symbol.isin}`
            );

            return symbolsChannels;
          },
          []
        );
      }

      return [];
    },
    onUpdate: (signal) => {
      const actions = {
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
        [CODES.SymbolStateChangeSignal]: transformSymbolTradingStateChange,
        [CODES.TseInstrumentClosingPriceChangeSignal]:
          transformClosingPriceChange,
        [CODES.SymbolTradePriceChangePreOpeningSignal]:
          transformPreOpeningTradePriceChange,
      };

      if (actions[signal.code]) {
        const data = actions[signal.code](signal.data);

        if (!result.value?.data?.result) {
          return;
        }

        const index = result.value.data.result.findIndex((s) => {
          return s.isin === data.isin;
        });

        if (index > -1) {
          const item = result.value.data.result[index];

          if (
            signal.code === CODES.SymbolTradePriceChangeSignal &&
            item.lastTradePrice !== data.lastTradePrice
          ) {
            Object.assign(item, data);
          } else if (signal.code !== CODES.SymbolTradePriceChangeSignal) {
            Object.assign(item, data);
          }
        }
      }
    },
  });

  const classificationSymbolsItems = computed(
    () => result.value?.data?.result || []
  );

  return {
    classificationSymbolsItems,
    getClassificationSymbolsItemList,
    isLoadingClassificationSymbolsItems,
    isSuccessLoadingClassificationSymbolsItems,
    isErrorLoadingClassificationSymbolsItems,
    classificationSymbolsItemsStatus,
  };
}
